'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from '@/i18n/navigation';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { localeNames, locales } from "@/config/i18n";
import { cn } from '@/lib/utils';

/**
 * 简化的语言选择器组件
 */
const LanguageSelector = ({
  variant = 'default',
  className = '',
  triggerClassName = '',
  contentClassName = '',
  showLabel = true,
  ...props
}) => {
  const router = useRouter();
  const [currentLocale, setCurrentLocale] = useState('en');
  const [mounted, setMounted] = useState(false);

  // 客户端挂载后获取当前语言
  useEffect(() => {
    setMounted(true);
    const path = window.location.pathname;
    for (const loc of locales) {
      if (path === `/${loc}` || path.startsWith(`/${loc}/`)) {
        setCurrentLocale(loc);
        break;
      }
    }
  }, []);

  // 语言切换处理
  const handleLanguageChange = (newLocale) => {
    try {
      setCurrentLocale(newLocale);
      const path = window.location.pathname;
      let newPath = path;
      
      // 移除当前语言前缀
      for (const loc of locales) {
        if (path === `/${loc}`) {
          newPath = '/';
          break;
        }
        if (path.startsWith(`/${loc}/`)) {
          newPath = path.substring(loc.length + 1);
          break;
        }
      }
      
      // 添加新语言前缀
      if (newLocale !== 'en') {
        newPath = `/${newLocale}${newPath}`;
      }
      
      window.location.href = newPath;
    } catch (error) {
      console.error('Language switch error:', error);
      window.location.reload();
    }
  };

  // 样式配置
  const styles = {
    default: {
      trigger: "inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-white bg-gray-900/60 border-green-400/30 rounded-md hover:bg-gray-800 hover:border-green-400/50 transition-all duration-300",
      content: "bg-gray-900/95 border-green-400/30 z-50",
      item: "cursor-pointer hover:bg-green-400/10 focus:bg-green-400/10"
    },
    sidebar: {
      trigger: "w-full justify-between bg-black/30 border-green-500/20 hover:border-green-500/40 text-white hover:bg-black/50 transition-all duration-300",
      content: "bg-black/95 border-green-500/30 z-50",
      item: "cursor-pointer hover:bg-green-500/10 focus:bg-green-500/10 text-gray-300 hover:text-white"
    },
    mobile: {
      trigger: "w-full h-14 text-sm bg-black/40 hover:bg-green-500/20 border border-green-400/30 hover:border-green-400/50 rounded-2xl text-gray-200 hover:text-white transition-all duration-300 justify-center shadow-lg hover:shadow-green-500/20 flex items-center",
      content: "bg-black/95 border border-green-400/30 rounded-xl shadow-xl backdrop-blur-xl z-[80]",
      item: "cursor-pointer hover:bg-green-500/10 text-gray-200 hover:text-white transition-colors duration-200"
    }
  };

  const variantStyle = styles[variant] || styles.default;

  // 如果还没有挂载，显示加载状态
  if (!mounted) {
    return (
      <div className={cn(
        variantStyle.trigger,
        triggerClassName,
        className
      )}>
        <div className="w-4 h-4 bg-gray-400 rounded animate-pulse" />
      </div>
    );
  }

  return (
    <Select 
      value={currentLocale} 
      onValueChange={handleLanguageChange} 
      {...props}
    >
      <SelectTrigger 
        className={cn(
          variantStyle.trigger,
          triggerClassName,
          className
        )}
        aria-label={`切换语言，当前：${localeNames[currentLocale]}`}
      >
        <SelectValue 
          placeholder={showLabel ? 'Language' : localeNames[currentLocale]} 
        />
      </SelectTrigger>
      <SelectContent 
        className={cn(variantStyle.content, contentClassName)}
      >
        {Object.keys(localeNames).map((key) => (
          <SelectItem 
            className={variantStyle.item}
            key={key} 
            value={key}
          >
            {localeNames[key]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

export default LanguageSelector; 